import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, DollarSign, Users, Calendar, 
  Download, Filter, BarChart3, PieChart,
  Activity, Target, Award, Clock
} from 'lucide-react';
import { agencyService } from '../../services';
import './AdminAnalytics.css';

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [selectedTab, setSelectedTab] = useState('overview');
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalRevenue: 0,
      totalBookings: 0,
      averageBookingValue: 0,
      conversionRate: 0,
      topSuppliers: [],
      revenueByMonth: [],
      totalQuotes: 0,
      totalClients: 0
    },
    agents: {
      topPerformers: [],
      performanceMetrics: {
        totalAgents: 0,
        activeAgents: 0,
        performingAgents: 0,
        performanceRate: 0
      },
      revenueByAgent: []
    },
    bookings: {
      bySupplier: [],
      byCategory: [],
      byMonth: [],
      averageLeadTime: 0
    },
    trends: {
      monthlyRevenue: [],
      monthlyBookings: [],
      supplierTrends: []
    }
  });

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);

      // Use agency analytics endpoint
      const data = await agencyService.getAnalytics({
        dateRange: parseInt(dateRange)
      });

      // Process the data from the API
      setAnalyticsData({
        overview: {
          totalRevenue: data.overview?.totalRevenue || 0,
          totalBookings: data.overview?.totalBookings || 0,
          averageBookingValue: data.overview?.averageBookingValue || 0,
          conversionRate: data.overview?.conversionRate || 0,
          topSuppliers: data.topSuppliers || [],
          revenueByMonth: data.revenueByMonth || [],
          totalQuotes: data.overview?.totalQuotes || 0,
          totalClients: data.overview?.totalClients || 0
        },
        agents: {
          topPerformers: data.topAgents || [],
          performanceMetrics: data.agentMetrics || {
            totalAgents: 0,
            activeAgents: 0,
            performingAgents: 0,
            performanceRate: 0
          },
          revenueByAgent: data.revenueByAgent || []
        },
        bookings: {
          bySupplier: data.topSuppliers || [],
          byCategory: data.bookingsByCategory || [],
          byMonth: data.revenueByMonth || [],
          averageLeadTime: data.averageLeadTime || 0
        },
        trends: {
          monthlyRevenue: data.revenueByMonth || [],
          monthlyBookings: data.bookingsByMonth || [],
          supplierTrends: data.supplierTrends || []
        }
      });



    } catch (error) {
      console.error('Failed to load analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const determineCategory = (supplier) => {
    // Simple categorization logic - enhance as needed
    if (!supplier) return 'Other';
    const lowerSupplier = supplier.toLowerCase();
    if (lowerSupplier.includes('cruise')) return 'Cruises';
    if (lowerSupplier.includes('hotel') || lowerSupplier.includes('marriott') || lowerSupplier.includes('hilton')) return 'Hotels';
    if (lowerSupplier.includes('disney') || lowerSupplier.includes('universal')) return 'Theme Parks';
    if (lowerSupplier.includes('delta') || lowerSupplier.includes('united') || lowerSupplier.includes('american')) return 'Flights';
    return 'Other';
  };

  const calculateAgentMetrics = (agents, agentPerformance) => {
    const activeAgents = agents.filter(a => a.status === 'active').length;
    const performingAgents = Object.keys(agentPerformance).length;
    
    return {
      totalAgents: agents.length,
      activeAgents,
      performingAgents,
      performanceRate: activeAgents > 0 ? (performingAgents / activeAgents * 100).toFixed(1) : 0
    };
  };

  const exportData = () => {
    // Convert analytics data to CSV
    const csvData = convertToCSV(analyticsData);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const convertToCSV = (data) => {
    // Simple CSV conversion - enhance as needed
    let csv = 'Analytics Report\n';
    csv += `Date Range: Last ${dateRange} days\n\n`;
    
    csv += 'Overview Metrics\n';
    csv += `Total Revenue,$${data.overview.totalRevenue.toFixed(2)}\n`;
    csv += `Total Bookings,${data.overview.totalBookings}\n`;
    csv += `Average Booking Value,$${data.overview.averageBookingValue.toFixed(2)}\n\n`;
    
    csv += 'Top Suppliers\n';
    csv += 'Supplier,Revenue,Bookings\n';
    data.overview.topSuppliers.forEach(s => {
      csv += `${s.supplier},$${s.revenue.toFixed(2)},${s.bookings}\n`;
    });
    
    return csv;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'agents', label: 'Agent Performance', icon: Users },
    { id: 'bookings', label: 'Bookings Analysis', icon: Activity },
    { id: 'clients', label: 'Client Insights', icon: Target }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics & Reports</h1>
            <p className="text-gray-600 mt-2">Track your agency's performance and insights</p>
          </div>
          <div className="flex items-center space-x-4 mt-4 sm:mt-0">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 12 months</option>
            </select>
            <button
              onClick={exportData}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b mb-8">
        <nav className="flex space-x-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`flex items-center pb-4 px-1 border-b-2 transition-colors ${
                selectedTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-5 h-5 mr-2" />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="analytics-content">
        {selectedTab === 'overview' && <OverviewTab data={analyticsData.overview} />}
        {selectedTab === 'agents' && <AgentsTab data={analyticsData.agents} />}
        {selectedTab === 'bookings' && <BookingsTab data={analyticsData.bookings} />}
        {selectedTab === 'clients' && <ClientsTab data={analyticsData.clients} />}
      </div>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ data }) => {
  const metrics = [
    { 
      label: 'Total Revenue', 
      value: `$${data.totalRevenue.toLocaleString()}`, 
      icon: DollarSign,
      color: 'green'
    },
    { 
      label: 'Total Bookings', 
      value: data.totalBookings, 
      icon: Activity,
      color: 'blue'
    },
    { 
      label: 'Average Booking Value', 
      value: `$${data.averageBookingValue.toFixed(0)}`, 
      icon: TrendingUp,
      color: 'purple'
    },
    { 
      label: 'Conversion Rate', 
      value: `${data.conversionRate}%`, 
      icon: Target,
      color: 'orange'
    }
  ];

  return (
    <div>
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {metrics.map((metric, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{metric.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{metric.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${metric.color}-100`}>
                <metric.icon className={`w-6 h-6 text-${metric.color}-600`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Revenue by Month */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
          <div className="h-64 flex items-center justify-center text-gray-500">
            {/* Placeholder for chart - integrate with a charting library */}
            <div className="text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>Revenue chart will be displayed here</p>
            </div>
          </div>
        </div>

        {/* Top Suppliers */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Suppliers</h3>
          <div className="space-y-4">
            {data.topSuppliers.map((supplier, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                    {index + 1}
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-gray-900">{supplier.supplier}</p>
                    <p className="text-sm text-gray-500">{supplier.bookings} bookings</p>
                  </div>
                </div>
                <p className="font-semibold text-gray-900">
                  ${supplier.revenue.toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Agents Tab Component
const AgentsTab = ({ data }) => {
  return (
    <div>
      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Total Agents</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.performanceMetrics.totalAgents}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Active Agents</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.performanceMetrics.activeAgents}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Performing Agents</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.performanceMetrics.performingAgents}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Performance Rate</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.performanceMetrics.performanceRate}%</p>
        </div>
      </div>

      {/* Top Performers */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Top Performing Agents</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bookings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Commission
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.topPerformers.map((agent, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full text-sm font-medium text-blue-600">
                      {index + 1}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{agent.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {agent.bookings}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${agent.revenue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${agent.averageCommission.toFixed(0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Bookings Tab Component
const BookingsTab = ({ data }) => {
  return (
    <div>
      {/* Bookings by Category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Bookings by Category</h3>
          <div className="space-y-4">
            {data.byCategory.map((category, index) => (
              <div key={index}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">{category.category}</span>
                  <span className="text-sm text-gray-500">{category.count} bookings</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full" 
                    style={{ width: `${(category.count / Math.max(...data.byCategory.map(c => c.count))) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Average Lead Time */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Booking Insights</h3>
          <div className="space-y-6">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Lead Time</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{data.averageLeadTime} days</p>
              <p className="text-sm text-gray-500 mt-1">From quote to booking</p>
            </div>
            <div className="pt-6 border-t">
              <p className="text-sm font-medium text-gray-600">Peak Booking Month</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">March</p>
              <p className="text-sm text-gray-500 mt-1">45 bookings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bookings by Supplier Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Bookings by Supplier</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bookings
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Value
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.bySupplier.map((supplier, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {supplier.supplier}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {supplier.bookings}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${supplier.revenue.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${(supplier.revenue / supplier.bookings).toFixed(0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Clients Tab Component
const ClientsTab = ({ data }) => {
  return (
    <div>
      {/* Client Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Total Clients</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.totalClients}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">New This Month</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.newClientsThisMonth}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Repeat Clients</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{data.repeatClients}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm font-medium text-gray-600">Retention Rate</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">
            {data.totalClients > 0 ? ((data.repeatClients / data.totalClients) * 100).toFixed(1) : 0}%
          </p>
        </div>
      </div>

      {/* Placeholder for future client insights */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Client Insights Coming Soon</h3>
          <p className="text-gray-500">
            Detailed client demographics, preferences, and travel patterns will be available here.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
